import React, { useEffect, useState } from "react";
import { formatDate, formatTime } from "../../../Utils/DateAndTime";
import { Dialog, DialogTitle, DialogContent, DialogActions,  Divider,  Avatar } from "@mui/material";
import { useRef } from "react";
import { motion } from "framer-motion";
import {
    Box,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    Typography,
    IconButton,
    InputAdornment,
    Chip,
    Button,
    TableHead,
    TableRow,
    TableContainer,
    TableCell,
    TableBody,
    Paper,
    Table,
    TableFooter,
    Autocomplete,
    TextField
} from "@mui/material";
import { Visibility, Edit, ContentCopy } from "@mui/icons-material";

import { GridPagination } from "@mui/x-data-grid";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useTheme } from "@mui/material/styles";

import { DatePicker } from "@mui/x-date-pickers";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import dayjs from "dayjs";
import { MAIN_URL } from "../../../Configurations/Urls";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import useEmployeeTaskViewerFilterStore from "../../../Zustand/Store/useEmployeeTaskViewerFilterStore";
import ClearIcon from "@mui/icons-material/Clear";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";

// Utilities
let formatDateV2 = (datestr) => {
    if (!datestr) return "-";
    let splitted = datestr.split("-");
    return `${splitted[2]}-${splitted[1]}-${splitted[0]}`;
};

const formatMinutes = (value) => {
    if (!value || value === 0) return "0m";

    value = Number(value);

    // Split minutes (before decimal) & seconds (after decimal)
    const minutes = Math.floor(value);
    const decimal = value - minutes;

    // Convert decimal fractional part into seconds (two digits max)
    const seconds = Math.round(decimal * 100);

    // Convert full minutes to hours/mins
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    let result = "";

    if (hours > 0) {
        result += `${hours}h `;
    }
    if (mins > 0) {
        result += `${mins}m `;
    }
    if (seconds > 0) {
        result += `${seconds}s`;
    }

    return result.trim() || "0m";
};

const getStatusStyles = (status) => {
    switch (status) {
        case "Pending":
            return { backgroundColor: "#FFF4CC", color: "#B38700" }; // yellow
        case "In Progress":
            return { backgroundColor: "#D6E4FF", color: "#084298" }; // blue
        case "In Review":
            return { backgroundColor: "#EDE7F6", color: "#5E35B1" }; // purple
        case "Approved":
            return { backgroundColor: "#D1F2EB", color: "#0E6251" }; // green
        case "Rejected":
            return { backgroundColor: "#FADBD8", color: "#922B21" }; // red
        case "Completed":
            return { backgroundColor: "#D4EFDF", color: "#1D8348" }; // dark green
        case "Blocked":
            return { backgroundColor: "#FDEDEC", color: "#C0392B" }; // strong red
        default:
            return { backgroundColor: "#E0E0E0", color: "#424242" }; // grey
    }
};


const EmployeeTaskViewer = ({heading}) => {
    const [open, setOpen] = useState(false);
  const [task, setTask] = useState(null);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const { userData, reporting_manager_employees, reporting_manager_interns } = useAuthStore();
    const org = userData?.organization;
    const loginUserOrgPersonId = userData?.loginuser?.[0]?.organization_person_id;

    const [employees, setEmployees] = useState([]);
    const [allPersons, setAllPersons] = useState([]);
    const {
        selectedPersonType,
        selectedEmployee,
        selectedDate,
        apiDate,
        pagination,
        setSelectedPersonType,
        setSelectedEmployee,
        setSelectedDate,
        setApiDate,
        setPagination,
    } = useEmployeeTaskViewerFilterStore();

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const isInitialMount = useRef(true);

    // Persist filters for this tab only (resets when tab/window closes)
    useEffect(() => {
        sessionStorage.setItem("et_selected_person_type", selectedPersonType);
    }, [selectedPersonType]);

    useEffect(() => {
        sessionStorage.setItem("et_selected_employee", selectedEmployee);
    }, [selectedEmployee]);

    useEffect(() => {
        if (apiDate) {
            sessionStorage.setItem("et_selected_date", apiDate.format("YYYY-MM-DD"));
        } else {
            sessionStorage.removeItem("et_selected_date");
        }
    }, [apiDate]);

    // ---------------- Fetch Employees --------------------
    const fetchEmployees = async () => {
        try {
           // const res = await axios.get(`${MAIN_URL}/api/organizations/employees?type=Intern`, {
            const res = await axios.get(`${MAIN_URL}/api/organizations/${org.organization_id}/persons-data-all`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
               
            });

            setAllPersons(res.data.persons || []);
        } catch (err) {
            console.error("Failed to fetch employees", err);
        }
    };

    // Filter employees based on selected person type - show all org employees/interns for reporting managers
    useEffect(() => {
        const filteredPersons = allPersons.filter(
            (person) => person.organization_person?.person_type_id === Number(selectedPersonType)
        );
        const sortedEmps = [...filteredPersons].sort((a, b) =>
            (`${a.first_name} ${a.last_name} - ${a.organization_person?.person_code}` || "").localeCompare(`${b.first_name} ${b.last_name} - ${b.organization_person?.person_code}` || "")
        );
        setEmployees(sortedEmps);
    }, [selectedPersonType, allPersons]);

    // Reset selected employee only when person type changes (and not on initial mount)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            setSelectedEmployee("");
        }
    }, [selectedPersonType]);

    // ---------------- Fetch Tasks ------------------------
    const fetchTasks = async () => {
        if (!org?.organization_id) return;
        setLoading(true);

        try {
            const params = {
                organization_id: org.organization_id,
                page: pagination.page + 1,
                per_page: pagination.pageSize,
                // Always send person_type_id to filter by Employee (1) or Intern (2)
                person_type_id: selectedPersonType || "1",
            };

            // Only send organization_person_id if a specific person is selected; otherwise show all org tasks
            if (selectedEmployee && selectedEmployee !== "") {
                params.organization_person_id = selectedEmployee;
            }
            
            if (apiDate) {
                params.date = apiDate.format("YYYY-MM-DD");
            }


            // Convert selectedDate (YYYY-MM-DD) → Date object
            const selected = new Date(selectedDate);

            // Cutoff: 26 Dec 2025 (month is 0-based)
            const cutoffDate = new Date(2025, 11, 26);

            // Normalize time (important!)
            selected.setHours(0, 0, 0, 0);
            cutoffDate.setHours(0, 0, 0, 0);

            const endpoint =
                selected >= cutoffDate
                    ? "employee-tasks-v1"
                    : "employee-tasks";

            const res = await axios.get(
                `${MAIN_URL}/api/project-management/project-tasks/${endpoint}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                    params,
                }
            );

            setTasks(res.data.data?.data || []);

            setPagination((prev) => ({
                ...prev,
                total: res.data.data?.total || 0,
            }));
        } catch (err) {
            console.error("Failed to fetch tasks", err);
        }

        setLoading(false);
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    // Auto-open date picker only on first-ever load (no saved date yet)
    useEffect(() => {
        const stored = sessionStorage.getItem("et_selected_date");
        if (!stored) setIsDatePickerOpen(true);
    }, []);

    useEffect(() => {
        fetchTasks();
    }, [selectedEmployee, apiDate, selectedPersonType, pagination.page, pagination.pageSize]);

    const commitDate = (value) => {
        if (!value) {
            setApiDate(null);
            setPagination((prev) => ({ ...prev, page: 0 }));
            lastCommittedRef.current = null;
            return;
        }
    
        const formatted = dayjs(value).format("YYYY-MM-DD");
    
        // prevent duplicate calls
        if (lastCommittedRef.current === formatted) return;
        lastCommittedRef.current = formatted;
    
        setApiDate(dayjs(value)); // 🔥 THIS triggers API via useEffect
        setPagination((prev) => ({ ...prev, page: 0 }));
    };
    const lastCommittedRef = useRef(
        selectedDate ? dayjs(selectedDate).format("YYYY-MM-DD") : null
    );
    const handleDragEnd = async (result) => {
        if (!result.destination) return;

        const updatedTasks = Array.from(tasks);

        const sourceIndex = result.source.index;
        const destIndex = result.destination.index;

        const sourceTask = updatedTasks[sourceIndex];
        const destTask = updatedTasks[destIndex];

        // Swap the correct field
        const temp = sourceTask.task_order;
        sourceTask.task_order = destTask.task_order;
        destTask.task_order = temp;

        // Reorder array visually
        updatedTasks.splice(sourceIndex, 1);
        updatedTasks.splice(destIndex, 0, sourceTask);

        setTasks(updatedTasks);

        // 🔥 API CALL
        try {
            await axios.post(
                `${MAIN_URL}/api/project-management/project-tasks/swap-order`,
                {
                    organization_id: org.organization_id,
                    task1_id: sourceTask.organization_project_task_id,
                    task1_order: sourceTask.task_order,
                    task2_id: destTask.organization_project_task_id,
                    task2_order: destTask.task_order,
                },
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("token")}`,
                    },
                }
            );
        } catch (err) {
            console.error("Order update failed", err);
        }
    };
    const TaskInfoDialog = ({ open, onClose, task }) => {
        const [categoryName, setCategoryName] = useState("");
        const [isCategoryLoading, setIsCategoryLoading] = useState(false);

        useEffect(() => {
            let isMounted = true;

            const loadCategory = async () => {
                if (!open) {
                    if (isMounted) {
                        setCategoryName("");
                        setIsCategoryLoading(false);
                    }
                    return;
                }

                // Prefer existing nested category if backend already included it.
                const existingName = task?.category?.task_category_name;
                if (existingName) {
                    if (isMounted) setCategoryName(existingName);
                    if (isMounted) setIsCategoryLoading(false);
                    return;
                }

                const categoryId = task?.organization_project_task_category_id;
                if (!categoryId) {
                    if (isMounted) setCategoryName("");
                    if (isMounted) setIsCategoryLoading(false);
                    return;
                }

                try {   
                    if (isMounted) setIsCategoryLoading(true);

                    const res = await axios.get(
                        `${MAIN_URL}/api/project-management/project-task-category/${categoryId}`,
                        {
                            headers: {
                                Authorization: `Bearer ${localStorage.getItem("token")}`,
                            },
                        }
                    );

                    const data = res?.data?.data || res?.data;
                    const name = data?.task_category_name || "";

                    if (isMounted) setCategoryName(name);
                } catch (err) {
                    console.error("Failed to fetch task category", err);
                    if (isMounted) setCategoryName("");
                } finally {
                    if (isMounted) setIsCategoryLoading(false);
                }
            };

            loadCategory();

            return () => {
                isMounted = false;
            };
        }, [open, task?.organization_project_task_category_id, task?.category?.task_category_name]);

        if (!task) return null;
        return (
          <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              component: motion.div,
              initial: { opacity: 0, scale: 0.9 },
              animate: { opacity: 1, scale: 1 },
              transition: { duration: 0.2 }
            }}
          >
            <DialogTitle sx={{ fontWeight: 600, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  <span>Task Details</span>
  <Typography
    onClick={() => {
      const editRoute = selectedPersonType === "2"
        ? `/project-task/interns/edit/${task.organization_project_task_id}`
        : `/project-task/edit-v2/${task.organization_project_task_id}`;
      navigate(editRoute);
    }}
    sx={{
      fontWeight: 400,
      fontSize: '0.9rem',
      color: '#1976D2',
      cursor: 'pointer',
      '&:hover': {
        textDecoration: 'underline',
        color: '#1565C0',
      }
    }}
  >
    View
  </Typography>
</DialogTitle>
      
            <DialogContent dividers>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Avatar>{task.assigned_organization_person.person.profile_image_url
                }</Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {task.assigned_organization_person.person.full_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assigned Employee
                  </Typography>
                </Box>
              </Box>
      
              <Divider sx={{ my: 2 }} />
      
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Task  Category
                </Typography>
                <Chip
                    label={
                        isCategoryLoading
                            ? "Loading..."
                            : categoryName || task?.category?.task_category_name || "—"
                    }
                    variant="outlined"
                />
              </Box>
      
              <Box mb={2}>
                <Typography variant="subtitle2" color="text.secondary">
                  Task Title
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {task.task_title}
                </Typography>
              </Box>
      
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Allocated Time
                </Typography>
                <Typography variant="body1">
                  {task.estimated_minutes } minutes
                </Typography>
              </Box>
            </DialogContent>
      
            <DialogActions>
              <Button onClick={onClose} variant="contained">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        );
      };

    // ---------------- Summary Totals ------------------------
    const totalAllocated = tasks.reduce(
        (sum, t) => sum + (t.total_estimated_minutes || 0),
        0
    );

    const totalLogged = tasks?.reduce(
        (sum, t) => {
            const val = t.calculated_actual_minutes;

            // Ignore null, undefined, 0, or negative values
            if (!val || val <= 0) return sum;

            return sum + val;
        },
        0
    );


    console.log("totalLogged is : ", totalLogged);

    console.log("tasks issss : ", tasks);


    let CopyFunction = async (id) => {
        // 1️⃣ Show loading toast
        const loadingToast = toast.loading("Copying the task, please wait...");

        try {
            const token = localStorage.getItem("token");

            const res = await axios.get(
                `${MAIN_URL}/api/project-management/project-tasks/${id}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            const data = res.data?.data;

            if (data) {
                let finalStructured = {
                    organization_id: data.organization_id || "",
                    organization_entity_id: data.organization_entity_id || "",
                    organization_project_id: data.organization_project_id || "",
                    organization_project_milestone_id: data.organization_project_milestone_id || "",
                    organization_project_task_template_id: data.organization_project_task_template_id || "",
                    organization_project_task_category_id: data.organization_project_task_category_id || "",
                    organization_project_task_subcategory_id: data.organization_project_task_subcategory_id || "",
                    priority: data.priority || "",
                    task_title: data.task_title || "",
                    parent_task_id: data.parent_task_id || "",
                    organization_project_task_type_id: data.organization_project_task_type_id || "",
                    complexity_level: data.complexity_level || "",
                    quantity: data.quantity || "",
                    estimated_minutes: data.estimated_minutes || "",
                    assigned_employee_id: data.assigned_employee_id || "",
                    scheduled_date: formatDate(data.scheduled_date) || "",
                    scheduled_time: formatTime(data.scheduled_time) || "",
                    status_remarks: data.status_remarks || "",
                    schedules: data.schedules.map(sc => ({

                        organization_project_task_schedule_id: null,
                        organization_id: sc.organization_id || "",
                        'organization_entity_id': null,
                        'organization_project_task_id': sc.organization_project_task_id || null,
                        'scheduled_date': sc.scheduled_date.split("T")[0],
                        'allocated_minutes': sc.allocated_minutes,
                        'is_reassigned': sc.is_reassigned,
                        'task_status': sc.task_status,
                        'status_remarks': sc.status_remarks

                    }))
                };

                // 2️⃣ Remove loading toast
                toast.dismiss(loadingToast);

                // 3️⃣ Show success toast
                toast.success("Task copied successfully!.");

                // Navigate based on person type
                const addRoute = selectedPersonType === "2" 
                    ? "/project-task/interns/add" 
                    : "/project-task/add";
                
                navigate(addRoute, { state: { copiedTask: finalStructured } });
            }
        } catch (error) {
            toast.dismiss(loadingToast);
            toast.error("Failed to fetch project task.");
        }
    };


    // ---------------- Render ------------------------

    return (
        <Box sx={{ p: 2 }}>
            <TaskInfoDialog
        open={open}
        onClose={() => setOpen(false)}
        task={task}
      />
            <Typography variant="h5" gutterBottom>
             {heading}
            </Typography>
            <br />

            {/* Filters */}
            <Box display="flex" gap={2} alignItems="center" mb={2}>

                {/* DATE NAVIGATION */}
                <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                        onClick={() => {
                            const newDate = selectedDate
                                ? selectedDate.subtract(1, "day")
                                : dayjs().subtract(1, "day");
                        
                            setSelectedDate(newDate);
                            commitDate(newDate); // 🔥 important
                        }}
                    >
                        ◀
                    </IconButton>

                    <DatePicker
    format="DD-MM-YYYY"
    value={selectedDate}
    open={isDatePickerOpen}
    onOpen={() => setIsDatePickerOpen(true)}
    closeOnSelect={false}
    onChange={(newValue) => {
        // UI only
        setSelectedDate(newValue);
    }}
    onClose={() => {
        // Calendar selection / pressing Enter
        setIsDatePickerOpen(false);
        commitDate(selectedDate);
    }}
    slotProps={{
        field: {
            clearable: true,
            onClear: () => {
                setSelectedDate(null);
                commitDate(null);
            },
        },
        textField: {
            fullWidth: true,
            onBlur: () => {
                // 🔥 Manual typing finished here
                commitDate(selectedDate);
            }
        }
    }}
/>

                    <IconButton
                        onClick={() => {
                            const newDate = selectedDate
                                ? selectedDate.add(1, "day")
                                : dayjs().add(1, "day");
                        
                            setSelectedDate(newDate);
                            commitDate(newDate); // 🔥 important
                        }}
                    >
                        ▶
                    </IconButton>
                </Box>

                {/* Person Type Filter */}
                <FormControl sx={{ minWidth: 150 }}>
                    <InputLabel>Person Type</InputLabel>
                    <Select
                        value={selectedPersonType}
                        onChange={(e) => {
                            setSelectedPersonType(e.target.value);
                            setPagination((prev) => ({ ...prev, page: 0 }));
                        }}
                        label="Person Type"
                    >
                        <MenuItem value="1">Employee</MenuItem>
                        <MenuItem value="2">Intern</MenuItem>
                    </Select>
                </FormControl>

                {/* Employee Filter */}
                <Autocomplete
                    fullWidth
                    options={[
                        { label: `All ${selectedPersonType === "1" ? "Employees" : "Interns"}`, value: "" },
                        ...employees.map((emp) => ({
                            label: `${emp.first_name} ${emp.last_name} - ${emp.organization_person?.person_code}`,
                            value: emp.organization_person?.organization_person_id
                        }))
                    ]}
                    value={
                        selectedEmployee === ""
                            ? { label: `All ${selectedPersonType === "1" ? "Employees" : "Interns"}`, value: "" }
                            : employees.find((emp) => emp.organization_person?.organization_person_id === selectedEmployee)
                                ? {
                                    label: `${employees.find((emp) => emp.organization_person?.organization_person_id === selectedEmployee)?.first_name} ${employees.find((emp) => emp.organization_person?.organization_person_id === selectedEmployee)?.last_name} - ${employees.find((emp) => emp.organization_person?.organization_person_id === selectedEmployee)?.organization_person?.person_code}`,
                                    value: selectedEmployee
                                }
                                : { label: `All ${selectedPersonType === "1" ? "Employees" : "Interns"}`, value: "" }
                    }
                    onChange={(e, newValue) => {
                        setSelectedEmployee(newValue?.value || "");
                        setPagination((prev) => ({ ...prev, page: 0 }));
                    }}
                    getOptionLabel={(option) => option.label || ""}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    renderInput={(params) => (
                        <TextField {...params} label={`Select ${selectedPersonType === "1" ? "Employee" : "Intern"}`} />
                    )}
                />


            </Box>

            {/* Summary Boxes */}
            <Box display="flex" gap={2} mb={2}>
                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        bgcolor: "primary.light",
                        borderRadius: 1,
                        color: "primary.contrastText",
                    }}
                >
                    <Typography variant="caption">Total Tasks</Typography>
                    <Typography variant="h5">{pagination.total}</Typography>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        bgcolor: "success.light",
                        borderRadius: 1,
                        color: "success.contrastText",
                    }}
                >
                    <Typography variant="caption">Completed</Typography>
                    <Typography variant="h5">
                        {tasks.filter((t) => t.status === "Completed").length}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        p: 2,
                        bgcolor: "warning.light",
                        borderRadius: 1,
                        color: "warning.contrastText",
                    }}
                >
                    <Typography variant="caption">In Progress</Typography>
                    <Typography variant="h5">
                        {tasks.filter(
                            (t) => t.status === "In Progress" || t.status === "Paused"
                        ).length}
                    </Typography>
                </Box>
            </Box>



            <div style={{ width: "100%" }}>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="taskTable">
                        {(provided) => (
                            <TableContainer component={Paper}>
                                <Table ref={provided.innerRef} {...provided.droppableProps}>

                                    {/* HEADER */}
                                    <TableHead>
                                        <TableRow>
                                            <TableCell></TableCell>
                                            <TableCell><b>Project</b></TableCell>
                                            <TableCell><b>Task</b></TableCell>
                                            <TableCell><b>Qty</b></TableCell>
                                            <TableCell><b>Scheduled Date</b></TableCell>
                                            <TableCell><b>{selectedPersonType === "1" ? "Employee" : "Intern"}</b></TableCell>
                                            <TableCell><b>Allocated</b></TableCell>
                                            <TableCell><b>Logged</b></TableCell>
                                            <TableCell><b>Status</b></TableCell>
                                            <TableCell><b>Action</b></TableCell>
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        {tasks?.map((task, index) => (
                                            <Draggable
                                                draggableId={String(task.organization_project_task_id)}
                                                index={index}
                                                key={task.organization_project_task_id}
                                            >
                                                {(draggable, snapshot) => (
                                                    <TableRow
                                                        ref={draggable.innerRef}
                                                        {...draggable.draggableProps}
                                                        sx={{
                                                            cursor: snapshot.isDragging ? "grab" : "default",
                                                            transition: "background-color 0.2s ease",

                                                            // Hover effect
                                                            "&:hover": {
                                                                backgroundColor: "#f5f5f5",
                                                            },

                                                            // While dragging effect
                                                            backgroundColor: snapshot.isDragging ? "#eeeeee" : "inherit",
                                                        }}
                                                    >
                                                        {/* Only this cell acts as handle */}
                                                        <TableCell
                                                            {...draggable.dragHandleProps}
                                                            sx={{
                                                                cursor: "grab",
                                                            }}
                                                        >
                                                            ⋮⋮
                                                        </TableCell>

                                                        <TableCell>{task.project?.project_name || "-"}</TableCell>

                                                        <TableCell>{task.task_title}</TableCell>
                                                        <TableCell>{task.quantity}</TableCell>

                                                        <TableCell>{(task.scheduled_date)}</TableCell>

                                                        <TableCell>
                                                            {task.assigned_organization_person?.person
                                                                ? `${task.assigned_organization_person.person.first_name} ${task.assigned_organization_person.person.last_name}`
                                                                : "-"}
                                                        </TableCell>

                                                        <TableCell>
                                                            {formatMinutes(task.total_estimated_minutes)}
                                                        </TableCell>

                                                        <TableCell
                                                            width={"100px"}
                                                            sx={{
                                                                color:
                                                                    task.calculated_actual_minutes > task.total_estimated_minutes
                                                                        ? "red"
                                                                        : "green",
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            {formatMinutes(task.calculated_actual_minutes || 0)}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={task.status}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: 600,
                                                                    textTransform: "capitalize",
                                                                    ...getStatusStyles(task.status),
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <IconButton
                                                                color="primary"
                                                                size="small"
                                                                onClick={ () => {
                                                                    setTask(task)
                                                                    setOpen(true)
                                                                }}
                                                            >
                                                                <Visibility fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                color="secondary"
                                                                size="small"
                                                                onClick={() => CopyFunction(task.organization_project_task_id)}
                                                            >
                                                                <ContentCopy fontSize="small" />
                                                            </IconButton>
                                                            <IconButton
                                                                color="success"
                                                                size="small"
                                                                onClick={() => {
                                                                    const editRoute = selectedPersonType === "2"
                                                                        ? `/project-task/interns/edit/${task.organization_project_task_id}`
                                                                        : `/project-task/edit-v2/${task.organization_project_task_id}`;
                                                                    navigate(editRoute);
                                                                }}
                                                            >
                                                                <Edit fontSize="small" />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </Draggable>
                                        ))}
                            

                                        {provided.placeholder}
                                    </TableBody>
                                    <TableFooter>
                                        <TableRow
                                            sx={{
                                                bgcolor: theme.palette.action.hover,        // auto switches for dark/light
                                                borderTop: `1px solid ${theme.palette.divider}`,
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {/* Blank columns to align correctly */}
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />

                                            {/* Allocated Column */}
                                            <TableCell sx={{ fontWeight: "bold" }}>
                                                {formatMinutes(totalAllocated)}
                                            </TableCell>
                                            {/* Logged Column */}
                                            <TableCell sx={{ fontWeight: "bold" }}>
                                                {formatMinutes(totalLogged)}
                                            </TableCell>

                                            {/* Status column (blank) */}
                                            <TableCell />


                                            {/* Action Column (blank) */}
                                            <TableCell />
                                        </TableRow>
                                    </TableFooter>

                                </Table>
                            </TableContainer>
                        )}
                    </Droppable>
                </DragDropContext>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    p={2}
                    sx={{
                        borderTop: `1px solid ${theme.palette.divider}`,
                        background: theme.palette.background.paper,
                        borderRadius: 1,
                    }}
                >
                    {/* Rows Per Page */}
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2">Rows per page:</Typography>

                        <Select
                            size="small"
                            value={pagination.pageSize}
                            onChange={(e) =>
                                setPagination((prev) => ({
                                    ...prev,
                                    pageSize: e.target.value,
                                    page: 0,
                                }))
                            }
                            sx={{ width: 80 }}
                        >
                            {[5, 10, 20, 50, 100].map((size) => (
                                <MenuItem key={size} value={size}>
                                    {size}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    {/* Page Info */}
                    <Typography variant="body2">
                        Page <b>{pagination.page + 1}</b> of{" "}
                        <b>{Math.ceil(pagination.total / pagination.pageSize) || 1}</b>
                    </Typography>

                    {/* Navigation */}
                    <Box display="flex" alignItems="center" gap={1}>
                        {/* First Page */}
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={pagination.page === 0}
                            onClick={() =>
                                setPagination((prev) => ({ ...prev, page: 0 }))
                            }
                        >
                            ⏮
                        </Button>

                        {/* Previous */}
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={pagination.page === 0}
                            onClick={() =>
                                setPagination((prev) => ({ ...prev, page: prev.page - 1 }))
                            }
                        >
                            ◀
                        </Button>

                        {/* Page Numbers */}
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {Array.from({
                                length: Math.ceil(pagination.total / pagination.pageSize),
                            })
                                .slice(
                                    Math.max(0, pagination.page - 2),
                                    pagination.page + 3
                                )
                                .map((_, idx) => {
                                    const pageNum = Math.max(0, pagination.page - 2) + idx;

                                    return (
                                        <Button
                                            key={pageNum}
                                            variant={
                                                pagination.page === pageNum
                                                    ? "contained"
                                                    : "outlined"
                                            }
                                            size="small"
                                            onClick={() =>
                                                setPagination((prev) => ({
                                                    ...prev,
                                                    page: pageNum,
                                                }))
                                            }
                                            sx={{ minWidth: 36 }}
                                        >
                                            {pageNum + 1}
                                        </Button>
                                    );
                                })}
                        </Box>

                        {/* Next */}
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={
                                (pagination.page + 1) * pagination.pageSize >=
                                pagination.total
                            }
                            onClick={() =>
                                setPagination((prev) => ({ ...prev, page: prev.page + 1 }))
                            }
                        >
                            ▶
                        </Button>

                        {/* Last Page */}
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={
                                (pagination.page + 1) * pagination.pageSize >=
                                pagination.total
                            }
                            onClick={() =>
                                setPagination((prev) => ({
                                    ...prev,
                                    page:
                                        Math.ceil(pagination.total / pagination.pageSize) - 1,
                                }))
                            }
                        >
                            ⏭
                        </Button>
                    </Box>
                </Box>


            </div>


        </Box>
    );
};

export default EmployeeTaskViewer;



{/* <div style={{ width: "100%" }}>
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="taskTable">
                        {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps}>
                           
                                <DataGrid
                                    // onRowClick={(params) => navigate(`/project-task/view/${params.row.id}`)}
                                    rows={tasks?.map((task) => ({
                                        ...task,
                                        id: task.organization_project_task_id,
                                        order: task.order
                                    }))}
                                    columns={columns}

                                    loading={loading}
                                    pageSize={pagination.pageSize}
                                    page={pagination.page}
                                    rowCount={pagination.total}
                                    paginationMode="server"
                                    onPageChange={(newPage) => setPagination((prev) => ({ ...prev, page: newPage }))}
                                    onPageSizeChange={(newSize) => setPagination((prev) => ({ ...prev, pageSize: newSize, page: 0 }))} rowsPerPageOptions={[5, 10, 25, 50]}

                                    pagination={true}
                                    disableSelectionOnClick
                                    autoHeight
                                    slots={{
                                        footer: CustomFooter,
                                    }}
                                    getRowId={(row) => row.id}
                                    // 👇 required for drag
                                    componentsProps={{
                                        row: {
                                            component: (props) => {
                                                const index = props.row.index;

                                                return (
                                                    <Draggable
                                                        key={props.id}
                                                        draggableId={String(props.id)}
                                                        index={index}
                                                    >
                                                        {(draggable) => (
                                                            <div
                                                                ref={draggable.innerRef}
                                                                {...draggable.draggableProps}
                                                                {...draggable.dragHandleProps}
                                                            >
                                                                <div {...props} />
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                );
                                            },
                                        },
                                    }}
                                />


                                {provided.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div> */}